import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/rootReducer';
import { dateToUtc } from '../../../functions';
import { getDateDaysFromNow } from '../../fuctions';
import { getShipments } from '../api';
import { mapShipmentToStatus } from '../functions';
import { AdminShipment, GeneralShipment, ShipmentStatus } from '../interfaces';

export default function useShipmentHistory(isAdmin: boolean) {
  const  userData = useSelector((state: RootState) => state.mainAppData.userData);
  const initialState = {
    search: '',
    shipmentStartDate: getDateDaysFromNow(-14),
    shipmentEndDate: getDateDaysFromNow(1),
    userEmail: isAdmin ? '' : (userData?.username || ''),
    selectedJob: '',
    subAccountNumber: '',
    shipmentList: [],
    selectedStatus: ShipmentStatus.ALL,
    loadingShipments: false,
  };

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [search, setSearch] = useState<string>(initialState.search);
  const [shipmentStartDate, setShipmentStartDate] =
    useState<Date | null>(initialState.shipmentStartDate);
  const [shipmentEndDate, setShipmentEndDate] =
    useState<Date | null>(initialState.shipmentEndDate);
  const [userEmail, setUserEmail] = useState<string>(initialState.userEmail);
  const [selectedJob, setSelectedJob] = useState<string>(initialState.selectedJob);
  const [subAccountNumber, setSubAccountNumber] = useState<string>(initialState.subAccountNumber);
  const [shipmentList, setShipmentList] = useState<
    GeneralShipment[] | AdminShipment[]
  >(initialState.shipmentList);
  const [selectedStatus, setSelectedStatus] = useState<ShipmentStatus>(initialState.selectedStatus);
  const [loadingShipments, setLoadingShipments] = useState<boolean>(initialState.loadingShipments);

  const filteredShipmentList = shipmentList.filter((shipment) =>
    selectedStatus === ShipmentStatus.ALL || selectedStatus === mapShipmentToStatus(shipment)
  );

  const curPayload = {
    search,
    shipmentStartDate,
    shipmentEndDate,
    userEmail,
    selectedJob,
    subAccountNumber,
  };

  const fetchShipments = async (payload: any) => {
    const { shipments } = await getShipments(
      payload,
      setLoadingShipments,
      isAdmin,
    );
    setShipmentList(shipments);
  };
  
  const onChangeSearch = (newSearch: string) => {
    setSearch(newSearch);
    if (searchTimeoutRef?.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchShipments({
        ...curPayload,
        search: newSearch,
      });
    }, 500);
  };

  const onChangeShipmentDates = (dates: [Date | null, Date | null ]) => {
    const [start, end] = dates;
    setShipmentStartDate(start);
    setShipmentEndDate(end);
    fetchShipments({
      ...curPayload,
      shipmentStartDate: start,
      shipmentEndDate: end,
    });
  };

  const onChangeShipmentRawDates = (event: React.FocusEvent<HTMLInputElement>) => {
    const input = event.target.value;
    if (
      !input ||
      !input.match(/\d{4}-\d{2}-\d{2}\s-\s\d{4}-\d{2}-\d{2}/)
    ) {
      return;
    }
    const [start, end] = input.split(' - ').map((date: string) => {
      const parsedDate = dateToUtc(new Date(date.trim()));
      if (!parsedDate?.getTime()) {
        return null;
      }
      return parsedDate;
    });
    if (start && end) {
      setShipmentStartDate(start);
      setShipmentEndDate(end);
      fetchShipments({
        ...curPayload,
        shipmentStartDate: start,
        shipmentEndDate: end,
      });
    }
  };

  const onChangeUser = (email: string) => {
    setUserEmail(email);
    fetchShipments({
      ...curPayload,
      userEmail: email,
    });
  };

  const onSelectJob = (job: string) => {
    setSelectedJob(job);
    fetchShipments({
      ...curPayload,
      selectedJob: job,
    });
  };

  const onChangeSubAccountNumber = (newSubAccountNumber: string) => {
    setSubAccountNumber(newSubAccountNumber);
    fetchShipments({
      ...curPayload,
      subAccountNumber: newSubAccountNumber,
    });
  }

  const onChangeStatusFilter = (newStatus: ShipmentStatus) => {
    let updatedStatus = newStatus;
    if (newStatus as string === 'All') {
      updatedStatus = ShipmentStatus.ALL;
    }
    setSelectedStatus(updatedStatus);
  };

  useEffect(() => {
    setSearch(initialState.search);
    setShipmentStartDate(initialState.shipmentStartDate);
    setShipmentEndDate(initialState.shipmentEndDate);
    setUserEmail(initialState.userEmail);
    setSelectedJob(initialState.selectedJob);
    setShipmentList(initialState.shipmentList);
    setSelectedStatus(initialState.selectedStatus);
    setLoadingShipments(initialState.loadingShipments);

    fetchShipments(initialState);
  }, [isAdmin]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef?.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    search,
    onChangeSearch,
    shipmentStartDate,
    shipmentEndDate,
    onChangeShipmentDates,
    onChangeShipmentRawDates,
    userEmail,
    onChangeUser,
    selectedJob,
    onSelectJob,
    subAccountNumber,
    onChangeSubAccountNumber,
    shipmentList: filteredShipmentList,
    onChangeStatusFilter,
    selectedStatus,
    loadingShipments,
  }
}
